// frontend/src/pages/LeaveCalendar.js
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { CalendarDays, HelpCircle, Search, Filter, X, Users } from 'lucide-react';
import CalendarGrid from '../components/Leave/CalendarGrid';
import { useDepartmentCalendar, useCompanyCalendar } from '../hooks/useLeaveData';
import { configAPI } from '../services/api';
import { useQuery } from '@tanstack/react-query';
import { mapToGenericLeaveType, getGenericLeaveTypeColor } from '../utils/leaveTypeMapping';
import toast from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import { usePersistence } from '../hooks/usePersistence';
import { useStaffList } from '../hooks/useStaffData';
import GroupedDateLeaveModal from '../components/Leave/GroupedDateLeaveModal';

// Constants
const SEARCH_DEBOUNCE_MS = 300;
const STAFF_FILTER_PAGE_SIZE = 1000; // Reasonable limit - can be increased if needed

// Utility function to extract staff ID from application (handles various data structures)
const getStaffIdFromApp = (app) => {
  return app.leave_staff_id?.staff_id || 
         app.staff_id || 
         app.employee_staff_id || 
         '';
};

// Utility function to get employee name from application
const getEmployeeNameFromApp = (app) => {
  return app.employee_name || 
         (app.leave_staff_id?.first_name && app.leave_staff_id?.last_name
           ? `${app.leave_staff_id.first_name} ${app.leave_staff_id.last_name}`.trim()
           : '') ||
         '';
};

const LeaveCalendar = () => {
  const { user } = useAuth();
  const [scope, setScope] = useState('department'); // 'department' or 'company'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDateModal, setShowDateModal] = useState(false);
  
  // Search and filter state (persisted)
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedStaffIds, setSelectedStaffIds] = usePersistence('leave-calendar-selected-staff', []);
  const [showStaffFilter, setShowStaffFilter] = useState(false);
  const [staffFilterSearch, setStaffFilterSearch] = useState('');
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Check if user is HOD or Deputy HOD (needed for staff list scoping)
  const isHOD = useMemo(() => {
    if (!user) return false;
    const hodDepts = user.hod_departments || [];
    const deputyHodDepts = user.deputy_hod_departments || [];
    return hodDepts.length > 0 || deputyHodDepts.length > 0;
  }, [user]);
  
  // Get accessible departments for staff list scoping (by name, since serializer returns names)
  const accessibleDepartmentNames = useMemo(() => {
    if (scope === 'company') return null; // Company scope: show all staff
    if (!user) return [];
    
    const deptNames = new Set();
    // User's own department
    if (user.department?.department_name) {
      deptNames.add(user.department.department_name);
    }
    // HOD departments (array of department names)
    if (user.hod_departments && Array.isArray(user.hod_departments)) {
      user.hod_departments.forEach(name => {
        if (name) deptNames.add(name);
      });
    }
    // Deputy HOD departments (array of department names)
    if (user.deputy_hod_departments && Array.isArray(user.deputy_hod_departments)) {
      user.deputy_hod_departments.forEach(name => {
        if (name) deptNames.add(name);
      });
    }
    
    return deptNames.size > 0 ? Array.from(deptNames) : null;
  }, [scope, user]);
  
  // Fetch staff list for filter - lazy load only when filter is open
  // Note: We filter by department name client-side since API filter expects IDs but we have names
  const staffListParams = useMemo(() => {
    return {
      page_size: STAFF_FILTER_PAGE_SIZE,
      employment_status: 'active'
    };
  }, []);
  
  const { 
    data: staffListData = { results: [] }, 
    isLoading: staffListLoading,
    error: staffListError 
  } = useStaffList({
    ...staffListParams,
    enabled: showStaffFilter // Lazy load: only fetch when filter is open
  });
  
  // Filter and scope staff list
  const availableStaff = useMemo(() => {
    let staff = (staffListData.results || []).filter(staff => 
      staff.employment_classification?.toLowerCase() !== 'system' &&
      !['A0001', 'S0000'].includes(staff.staff_id)
    );
    
    // For department scope, filter by accessible department names (client-side)
    if (scope === 'department' && accessibleDepartmentNames && accessibleDepartmentNames.length > 0) {
      const accessibleNamesSet = new Set(accessibleDepartmentNames);
      staff = staff.filter(s => {
        const deptName = s.department?.department_name;
        return deptName && accessibleNamesSet.has(deptName);
      });
    }
    
    return staff;
  }, [staffListData, scope, accessibleDepartmentNames]);
  
  // Filter staff list for the dropdown search
  const filteredStaffForDropdown = useMemo(() => {
    if (!staffFilterSearch.trim()) return availableStaff;
    
    const search = staffFilterSearch.toLowerCase().trim();
    return availableStaff.filter(staff => {
      const name = `${staff.first_name || ''} ${staff.last_name || ''}`.toLowerCase();
      const staffId = (staff.staff_id || '').toLowerCase();
      const department = (staff.department?.department_name || '').toLowerCase();
      return name.includes(search) || staffId.includes(search) || department.includes(search);
    });
  }, [availableStaff, staffFilterSearch]);
  
  // Create Set for O(1) lookups (performance optimization)
  const selectedStaffIdsSet = useMemo(() => new Set(selectedStaffIds), [selectedStaffIds]);

  // Determine if we should show actual leave types
  // Only show actual types in department scope if user is HOD
  // Company scope always shows generic labels
  const showActualLeaveTypes = useMemo(() => {
    return scope === 'department' && isHOD;
  }, [scope, isHOD]);

  // Badge renderer for the date detail modal (leave type pill)
  const renderCalendarBadge = useCallback((app) => {
    const leaveTypeName = app.leave_type_name ||
      app.leave_type?.leave_type_name ||
      app.leave_type_id?.leave_type_name || 'Unknown';
    const displayLeaveType = showActualLeaveTypes
      ? leaveTypeName
      : mapToGenericLeaveType(leaveTypeName);
    const badgeClass = getGenericLeaveTypeColor(displayLeaveType);
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${badgeClass}`}>
        {displayLeaveType}
      </span>
    );
  }, [showActualLeaveTypes]);

  // Calculate date range for current month (using local date to avoid timezone issues)
  const monthStart = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const date = new Date(year, month, 1);
    // Format as YYYY-MM-DD using local date (not UTC) to avoid timezone shifts
    const yearStr = date.getFullYear();
    const monthStr = String(date.getMonth() + 1).padStart(2, '0');
    const dayStr = String(date.getDate()).padStart(2, '0');
    return {
      date: date,
      dateStr: `${yearStr}-${monthStr}-${dayStr}`
    };
  }, [currentDate]);

  const monthEnd = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const date = new Date(year, month + 1, 0); // Last day of month
    // Format as YYYY-MM-DD using local date (not UTC) to avoid timezone shifts
    const yearStr = date.getFullYear();
    const monthStr = String(date.getMonth() + 1).padStart(2, '0');
    const dayStr = String(date.getDate()).padStart(2, '0');
    return {
      date: date,
      dateStr: `${yearStr}-${monthStr}-${dayStr}`
    };
  }, [currentDate]);

  // Fetch calendar data based on scope - only fetch the active scope
  const { data: departmentData = [], isLoading: deptLoading, error: deptError } = useDepartmentCalendar({
    start_date: monthStart.dateStr,
    end_date: monthEnd.dateStr,
  }, {
    enabled: scope === 'department', // Only fetch when department tab is active
    keepPreviousData: true, // Prevent flickering when switching views
  });

  const { data: companyData = [], isLoading: companyLoading, error: companyError } = useCompanyCalendar({
    start_date: monthStart.dateStr,
    end_date: monthEnd.dateStr,
  }, {
    enabled: scope === 'company', // Only fetch when company tab is active
    keepPreviousData: true, // Prevent flickering when switching views
  });

  // Get applications based on scope (defined early for use in other hooks)
  const applications = scope === 'department' ? departmentData : companyData;
  const isLoading = scope === 'department' ? deptLoading : companyLoading;
  const error = scope === 'department' ? deptError : companyError;

  // Get valid staff IDs from applications (for validation)
  const validStaffIdsSet = useMemo(() => {
    const ids = new Set();
    applications.forEach(app => {
      const id = getStaffIdFromApp(app);
      if (id) ids.add(id);
    });
    return ids;
  }, [applications]);

  // Validate and clean selected staff IDs (remove invalid selections)
  useEffect(() => {
    if (selectedStaffIds.length > 0 && validStaffIdsSet.size > 0) {
      const invalidIds = selectedStaffIds.filter(id => !validStaffIdsSet.has(id));
      if (invalidIds.length > 0) {
        // Auto-remove invalid selections (they don't match any applications)
        setSelectedStaffIds(prev => prev.filter(id => validStaffIdsSet.has(id)));
        if (invalidIds.length === selectedStaffIds.length) {
          // All were invalid - show message
          toast.error('Selected staff members have no leave applications in this period');
        }
      }
    }
  }, [selectedStaffIds, validStaffIdsSet, setSelectedStaffIds]);

  // Filter applications based on search and staff selection (optimized with Set)
  const filteredApplications = useMemo(() => {
    let filtered = applications;
    
    // Filter by selected staff IDs (using Set for O(1) lookup)
    if (selectedStaffIdsSet.size > 0) {
      filtered = filtered.filter(app => {
        const staffId = getStaffIdFromApp(app);
        return selectedStaffIdsSet.has(staffId);
      });
    }
    
    // Filter by search term (name or staff ID)
    if (debouncedSearchTerm.trim()) {
      const search = debouncedSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(app => {
        const employeeName = getEmployeeNameFromApp(app).toLowerCase();
        const staffId = getStaffIdFromApp(app).toLowerCase();
        return employeeName.includes(search) || staffId.includes(search);
      });
    }
    
    return filtered;
  }, [applications, selectedStaffIdsSet, debouncedSearchTerm]);

  // Fetch public holidays
  const { data: publicHolidaysRaw = [] } = useQuery({
    queryKey: ['public-holidays', currentDate.getFullYear()],
    queryFn: async () => {
      try {
        const response = await configAPI.getPublicHolidays({ year: currentDate.getFullYear() });
        return response.data?.results || response.data || [];
      } catch (error) {
        // Silently fail - public holidays are not critical for calendar functionality
        return [];
      }
    },
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  // Normalize public holidays to {date, name} format
  const publicHolidays = useMemo(() => {
    const holidayMap = new Map();
    
    publicHolidaysRaw.forEach(h => {
      const primary = h.date || h.holiday_date || h.public_holiday_date;
      const observed = h.observed_date || h.observed_on;
      const start = h.start_date;
      const end = h.end_date;
      const holidayName = h.holiday_name || h.name || 'Public Holiday';

      if (start && end) {
        // Expand range inclusively
        const s = new Date(start);
        const e = new Date(end);
        const cursor = new Date(s.getFullYear(), s.getMonth(), s.getDate());
        while (cursor <= e) {
          const y = cursor.getFullYear();
          const m = String(cursor.getMonth() + 1).padStart(2, '0');
          const d = String(cursor.getDate()).padStart(2, '0');
          const dateStr = `${y}-${m}-${d}`;
          holidayMap.set(dateStr, holidayName);
          cursor.setDate(cursor.getDate() + 1);
        }
      } else {
        if (primary) {
          const only = primary.includes('T') ? primary.split('T')[0] : primary;
          holidayMap.set(only, holidayName);
        }
        if (observed) {
          const only = observed.includes('T') ? observed.split('T')[0] : observed;
          holidayMap.set(only, holidayName);
        }
      }
    });

    return Array.from(holidayMap.entries()).map(([date, name]) => ({ date, name }));
  }, [publicHolidaysRaw]);


  // Handle month navigation
  const handleMonthChange = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
    setSelectedDate(null);
  };

  // Handle today button
  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Handle date click - inline panel on mobile, modal on desktop
  const handleDateClick = (date) => {
    setSelectedDate(date);
    if (window.matchMedia('(min-width: 640px)').matches) {
      setShowDateModal(true);
    }
  };

  // Get applications for selected date (use filtered applications)
  const selectedDateApplications = useMemo(() => {
    if (!selectedDate) return [];
    
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    return filteredApplications.filter(app => {
      if (app.leave_status !== 'approved' && app.leave_status !== 'auto_approved') return false;
      const startDateStr = app.leave_start_date ? app.leave_start_date.split('T')[0] : '';
      const endDateStr = app.leave_end_date ? app.leave_end_date.split('T')[0] : '';
      return dateStr >= startDateStr && dateStr <= endDateStr;
    });
  }, [selectedDate, filteredApplications]);
  
  // Handle staff filter toggle
  const handleStaffToggle = (staffId) => {
    setSelectedStaffIds(prev => {
      if (prev.includes(staffId)) {
        return prev.filter(id => id !== staffId);
      } else {
        return [...prev, staffId];
      }
    });
  };
  
  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedStaffIds([]);
  };
  
  // Get staff name by ID
  const getStaffName = (staffId) => {
    const staff = availableStaff.find(s => s.staff_id === staffId);
    if (!staff) return staffId;
    return `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || staff.staff_id;
  };

  // Show error if any
  React.useEffect(() => {
    if (error) {
      toast.error(`Failed to load calendar data: ${error?.message || 'Unknown error'}`);
    }
  }, [error]);

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Page Header */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8">
          <div className="flex items-center gap-3 sm:gap-4 mb-2 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5 text-blue-800" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Leave Calendar</h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">View team and company leave schedules</p>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        {isLoading ? (
          <div className="bg-white rounded-2xl shadow-sm border p-6 animate-pulse">
            {/* Calendar Header Skeleton */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="h-10 bg-gray-200 rounded-full w-32"></div>
                <div className="h-6 bg-gray-200 rounded w-48"></div>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-10 bg-gray-200 rounded-full w-10"></div>
                <div className="h-10 bg-gray-200 rounded-full w-10"></div>
              </div>
            </div>
            
            {/* Calendar Grid Skeleton */}
            <div className="space-y-2">
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                  <div key={i} className="h-8 bg-gray-100 rounded text-center flex items-center justify-center">
                    <div className="h-4 bg-gray-200 rounded w-8"></div>
                  </div>
                ))}
              </div>
              
              {/* Calendar Days Grid */}
              <div className="grid grid-cols-7 gap-2">
                {Array.from({ length: 35 }).map((_, i) => (
                  <div key={i} className="aspect-square bg-gray-50 rounded-lg p-2 border border-gray-100">
                    <div className="h-4 bg-gray-200 rounded w-6 mb-2"></div>
                    <div className="space-y-1">
                      <div className="h-3 bg-gray-200 rounded w-full"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : filteredApplications.length === 0 && (searchTerm || selectedStaffIds.length > 0) ? (
          <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm && selectedStaffIds.length > 0
                ? `No leave applications match your search "${searchTerm}" and selected staff filters.`
                : searchTerm
                ? `No leave applications match your search "${searchTerm}".`
                : 'No leave applications found for the selected staff members.'}
            </p>
            <button
              onClick={handleClearFilters}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          <CalendarGrid
            applications={filteredApplications}
            publicHolidays={publicHolidays}
            currentDate={currentDate}
            selectedDate={selectedDate}
            onDateClick={handleDateClick}
            onMonthChange={handleMonthChange}
            onTodayClick={handleToday}
            showLegend={true}
            scope={scope}
            onScopeChange={setScope}
            showActualLeaveTypes={showActualLeaveTypes}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedStaffIds={selectedStaffIds}
            onStaffFilterToggle={() => setShowStaffFilter(!showStaffFilter)}
            showStaffFilter={showStaffFilter}
            onStaffFilterClose={() => setShowStaffFilter(false)}
            staffFilterSearch={staffFilterSearch}
            onStaffFilterSearchChange={setStaffFilterSearch}
            availableStaff={availableStaff}
            filteredStaffForDropdown={filteredStaffForDropdown}
            selectedStaffIdsSet={selectedStaffIdsSet}
            onStaffToggle={handleStaffToggle}
            onClearFilters={handleClearFilters}
            staffListLoading={staffListLoading}
            staffListError={staffListError}
            getStaffName={getStaffName}
            userDepartment={user?.department?.department_name || ''}
          />
        )}

        {/* Legend */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="flex items-center gap-2 mb-4">
            <HelpCircle className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Leave Type Legend</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { label: 'Out of Office', desc: 'Annual Leave, OIL, Marriage, Unpaid, Childcare, Compassionate Leave' },
              { label: 'Unavailable', desc: 'Outpatient Sick Leave, Hospitalisation Leave' },
              { label: 'Extended Leave', desc: 'Maternity, Paternity, Shared Parental Leave' },
              { label: 'Work From Home', desc: 'Work From Home, Flexi-Place arrangements' },
              { label: 'External Activity', desc: 'Offsite meetings, seminars, workshops, client visits' },
              { label: 'On Course', desc: 'Company-sanctioned training, course, or academic programme' },
              { label: 'NS Leave', desc: 'National Service reservist training and duties' },
            ].map((item) => {
              const colorClass = getGenericLeaveTypeColor(item.label);
              return (
                <div key={item.label} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`w-4 h-4 rounded border flex-shrink-0 mt-0.5 ${colorClass}`}></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.label}</p>
                    <p className="text-xs text-gray-600 mt-1">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Date Details Modal */}
        <GroupedDateLeaveModal
          show={showDateModal}
          onClose={() => setShowDateModal(false)}
          date={selectedDate}
          applications={selectedDateApplications}
          userDepartment={user?.department?.department_name}
          renderBadge={renderCalendarBadge}
        />
    </div>
  );
};

export default LeaveCalendar;

